import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckSquare, Music, FolderOpen, Megaphone, ArrowRight, PoundSterling, Newspaper, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, selectedArtist }) => {
  const [stats, setStats] = useState({
    tasks: 0,
    completedTasks: 0,
    shows: 0,
    files: 0,
    diary: 0,
    invoices: 0,
    news: 0,
  });
  const [recentTasks, setRecentTasks] = useState([]);
  const [upcomingShows, setUpcomingShows] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentNews, setRecentNews] = useState([]);

  // Get user permissions
  const permissions = user?.effective_permissions || user?.permissions || {};
  const hasPermission = (key) => permissions[key] !== false;

  useEffect(() => {
    fetchDashboardData();
  }, [selectedArtist]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = selectedArtist ? `?artist_id=${selectedArtist}` : '';

      const [tasksRes, showsRes, filesRes, diaryRes, invoicesRes, newsRes] = await Promise.all([
        axios.get(`${API}/tasks${params}`, { headers }),
        axios.get(`${API}/live-shows${params}`, { headers }),
        axios.get(`${API}/files${params}`, { headers }),
        axios.get(`${API}/diary${params}`, { headers }),
        axios.get(`${API}/invoices${params}`, { headers }),
        axios.get(`${API}/news/saved${params}`, { headers }),
      ]);

      const tasks = tasksRes.data;
      const shows = showsRes.data;
      const invoices = invoicesRes.data;
      const news = newsRes.data;

      setStats({
        tasks: tasks.length,
        completedTasks: tasks.filter((t) => t.completed).length,
        shows: shows.length,
        files: filesRes.data.length,
        diary: diaryRes.data.length,
        invoices: invoices.length,
        news: news.length,
      });

      setRecentTasks(tasks.slice(0, 5));
      setUpcomingShows(shows.slice(0, 3));
      setRecentInvoices(invoices.slice(0, 3));
      setRecentNews(news.slice(0, 3));
    } catch (error) { // Silent fail
      
    }
  };

  const statCards = [
    {
      title: 'Active Tasks',
      value: stats.tasks - stats.completedTasks,
      total: stats.tasks,
      icon: CheckSquare,
      color: 'from-violet-600 to-purple-600',
      link: '/tasks',
      permKey: 'tasks',
    },
    {
      title: 'Live Shows',
      value: stats.shows,
      icon: Music,
      color: 'from-blue-600 to-cyan-600',
      link: '/live-shows',
      bgColor: 'bg-blue-500/5',
      permKey: 'live_shows',
    },
    {
      title: 'Files',
      value: stats.files,
      icon: FolderOpen,
      color: 'from-emerald-600 to-teal-600',
      link: '/files',
      bgColor: 'bg-emerald-500/5',
      permKey: 'files',
    },
    {
      title: 'Diary',
      value: stats.diary,
      icon: Megaphone,
      color: 'from-orange-600 to-red-600',
      link: '/diary',
      bgColor: 'bg-orange-500/5',
      permKey: 'campaigns',
    },
    {
      title: 'Invoices',
      value: stats.invoices,
      icon: PoundSterling,
      color: 'from-green-600 to-emerald-600',
      link: '/invoices',
      bgColor: 'bg-green-500/5',
      permKey: 'invoices',
    },
    {
      title: 'News Articles',
      value: stats.news,
      icon: Newspaper,
      color: 'from-indigo-600 to-blue-600',
      link: '/news',
      bgColor: 'bg-indigo-500/5',
      permKey: 'news',
    },
  ].filter(stat => hasPermission(stat.permKey));

  return (
    <div className="space-y-5" data-testid="dashboard">
      {/* Welcome Section - Compact */}
      <div>
        <h1 className="font-manrope font-bold text-2xl text-foreground mb-1">
          Welcome back, {user?.name}!
        </h1>
        <p className="text-muted-foreground text-sm">Here&apos;s what&apos;s happening with your projects</p>
      </div>

      {/* Stats Grid - Reduced Gap */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat) => (
          <Link key={stat.title} to={stat.link}>
            <Card
              className={`relative overflow-hidden ${stat.bgColor || 'bg-card'} border border-border/50 hover:border-primary/50 shadow-lg backdrop-blur-sm transition-all duration-300 group`}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
            >
              <div className="relative p-4 flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-manrope font-medium text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      {stat.title}
                    </p>
                    <p className="font-manrope font-bold text-3xl text-foreground">
                      {stat.value}
                      {stat.total && <span className="text-lg text-muted-foreground">/{stat.total}</span>}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.color} bg-opacity-20`}>
                    <stat.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-primary group-hover:gap-3 transition-all">
                  <span className="text-xs font-manrope font-medium">View details</span>
                  <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Tasks */}
        <Card className="bg-card border border-border/50 shadow-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-manrope font-bold text-lg text-foreground">Recent Tasks</h2>
            <Link to="/tasks">
              <Button variant="ghost" size="sm" data-testid="view-all-tasks-button">
                View all
              </Button>
            </Link>
          </div>
          <div className="space-y-2">
            {recentTasks.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4">No tasks yet</p>
            ) : (
              recentTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent/50 transition-colors"
                  data-testid={`recent-task-${task.id}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${
                      task.completed ? 'bg-green-500' : 'bg-yellow-500'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{task.title}</p>
                    {task.due_date && (
                      <p className="text-xs text-muted-foreground">
                        Due: {new Date(task.due_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Upcoming Shows */}
        <Card className="bg-card border border-border/50 shadow-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-manrope font-bold text-lg text-foreground">Upcoming Shows</h2>
            <Link to="/live-shows">
              <Button variant="ghost" size="sm" data-testid="view-all-shows-button" className="text-xs">
                View all
              </Button>
            </Link>
          </div>
          <div className="space-y-2">
            {upcomingShows.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4">No shows scheduled</p>
            ) : (
              upcomingShows.map((show) => (
                <div
                  key={show.id}
                  className="p-3 rounded-lg bg-accent/20 border border-border/50"
                  data-testid={`upcoming-show-${show.id}`}
                >
                  <p className="font-manrope font-semibold text-sm text-foreground">{show.venue}</p>
                  <p className="text-xs text-muted-foreground">
                    {show.city}, {show.country}
                  </p>
                  <p className="text-xs text-primary mt-1">
                    {new Date(show.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Recent Invoices - Only show if user has permission */}
        {hasPermission('invoices') && (
        <Card className="bg-card border border-border/50 shadow-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-manrope font-bold text-lg text-foreground">Recent Invoices</h2>
            <Link to="/invoices">
              <Button variant="ghost" size="sm" data-testid="view-all-invoices-button">
                View all
              </Button>
            </Link>
          </div>
          <div className="space-y-2">
            {recentInvoices.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4">No invoices yet</p>
            ) : (
              recentInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="p-3 rounded-lg bg-accent/20 border border-border/50"
                  data-testid={`recent-invoice-${invoice.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-manrope font-semibold text-sm text-foreground">{invoice.invoice_number}</p>
                    <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'} className="text-xs">
                      {invoice.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1">{invoice.description}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-muted-foreground capitalize">{invoice.type}</span>
                    <span className={`font-mono text-xs font-bold ${
                      invoice.type === 'income' ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {invoice.type === 'income' ? '+' : '-'}£{invoice.amount.toFixed(2)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
        )}

        {/* Recent News - Only show if user has permission */}
        {hasPermission('news') && (
        <Card className="bg-card border border-border/50 shadow-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-manrope font-bold text-lg text-foreground">Saved News</h2>
            <Link to="/news">
              <Button variant="ghost" size="sm" data-testid="view-all-news-button" className="text-xs">
                View all
              </Button>
            </Link>
          </div>
          <div className="space-y-2">
            {recentNews.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4">No saved articles yet</p>
            ) : (
              recentNews.map((article) => (
                <div
                  key={article.id}
                  className="p-3 rounded-lg bg-accent/20 border border-border/50"
                  data-testid={`recent-news-${article.id}`}
                >
                  <p className="font-manrope font-semibold text-sm text-foreground line-clamp-2 mb-1">{article.title}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{article.publisher}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(article.published_date || article.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;